import React from 'react';
import { warn, error as logError } from '../utils/logger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  trackError = async (error, errorInfo, errorId) => {
    try {
      // Get session ID from localStorage (same pattern as useAnalytics)
      let sessionId = localStorage.getItem('analytics_session_id');
      if (!sessionId) {
        sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('analytics_session_id', sessionId);
      }

      const response = await fetch('/api/analytics/event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId
        },
        body: JSON.stringify({
          eventType: 'error',
          eventData: {
            errorId: errorId,
            errorMessage: error.toString(),
            errorStack: error.stack,
            componentStack: errorInfo.componentStack,
            errorBoundary: true
          },
          page: window.location.pathname,
          category: 'error_boundary',
          success: false,
          error: error.toString()
        })
      });

      if (!response.ok) {
        warn('Failed to track error');
      }
    } catch (trackingErr) {
      warn('Error tracking error', { error: trackingErr.message, stack: trackingErr.stack });
    }
  };

  componentDidCatch(err, errorInfo) {
    // Log error details
    const errorId = Date.now().toString(36);

    this.setState({
      error: err,
      errorInfo: errorInfo,
      errorId: errorId
    });

    // Track error to analytics
    this.trackError(err, errorInfo, errorId);

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      logError('Error Boundary caught an error', {
        error: err.message,
        stack: err.stack,
        componentStack: errorInfo.componentStack
      });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom error UI
      return (
        <div style={{
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          background: 'var(--color-background)',
          color: 'var(--color-text-primary)'
        }}>
          <div style={{
            textAlign: 'center',
            maxWidth: '500px',
            background: 'var(--color-surface)',
            padding: '2rem',
            borderRadius: '16px',
            boxShadow: 'var(--shadow-medium)'
          }}>
            <div style={{
              fontSize: '3rem',
              marginBottom: '1rem'
            }}>
              😵
            </div>
            
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              marginBottom: '1rem',
              color: 'var(--color-text-primary)'
            }}>
              {this.props.title || 'Something went wrong'}
            </h2>
            
            <p style={{
              color: 'var(--color-text-secondary)',
              marginBottom: '1.5rem',
              lineHeight: '1.5'
            }}>
              {this.props.message || 
                'We encountered an unexpected error. Our team has been notified and is working on a fix.'
              }
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                textAlign: 'left',
                marginBottom: '1.5rem',
                padding: '1rem',
                background: 'var(--color-background-secondary)',
                borderRadius: '8px',
                fontSize: '0.875rem'
              }}>
                <summary style={{ cursor: 'pointer', fontWeight: '500' }}>
                  Error Details (Development)
                </summary>
                <pre style={{
                  marginTop: '0.5rem',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.75rem',
                  color: 'var(--color-accent-red)'
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={this.handleRetry}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--color-accent-blue)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  minHeight: '44px'
                }}
                onMouseOver={(e) => e.target.style.background = 'var(--color-accent-blue-hover)'}
                onMouseOut={(e) => e.target.style.background = 'var(--color-accent-blue)'}
              >
                Try Again
              </button>
              
              <button
                onClick={this.handleReload}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--color-surface-secondary)',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  minHeight: '44px'
                }}
                onMouseOver={(e) => e.target.style.background = 'var(--color-background-secondary)'}
                onMouseOut={(e) => e.target.style.background = 'var(--color-surface-secondary)'}
              >
                Reload Page
              </button>
            </div>

            {this.state.errorId && (
              <p style={{
                marginTop: '1rem',
                fontSize: '0.75rem',
                color: 'var(--color-text-tertiary)'
              }}>
                Error ID: {this.state.errorId}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easier usage
export const withErrorBoundary = (WrappedComponent, errorBoundaryProps = {}) => {
  return function WithErrorBoundaryComponent(props) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
};

export default ErrorBoundary;
